import { MigrationWavesService } from '@amzn/awscat-portfolio-assessment-service-client';
import { MigrationPeriod, MigrationWave } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Box, Checkbox, ColumnLayout, DatePicker, FormField,
    Input, RadioGroup, SpaceBetween, Spinner, Table, TableProps } from '@amzn/awsui-components-react';
import { AxiosError } from 'axios';
import { validate, ValidationError } from 'class-validator';
import { useState } from 'react';

import { DATE_PLACEHOLDER, toUTCShortDate } from '../../../common/DateDisplay';
import { constants } from '../../../common/Localization';
import { AxiosErrorResponse } from '../../../common/types';
import { errorLookup, GetErrorText } from '../../../common/validator/ErrorLookup';
import { DEFAULT_APP_CAPACITY, DEFAULT_DB_CAPACITY, DEFAULT_SERVER_CAPACITY, START_DAY_OPTIONS, WAVE_END_TIME, WAVE_START_TIME } from './constants';
import { AutoCreateWaveState, MigrationPeriodToSave } from './interface';

type Props = {
    autoCreateDetails: AutoCreateWaveState;
    setAutoCreateDetails: (details: AutoCreateWaveState) => void;
    portfolioId: string;
};

const migrationWavesService = new MigrationWavesService();

/**
 * Allow users to automatically create waves
 * When users select a start or end date, the date in created in UTC at a set time: 00:00:00 for start, 23:59:59 for end
 * When dates are displayed to users it is in UTC to be consistent across timezones
 */
function AutoCreateWaves({ autoCreateDetails, setAutoCreateDetails, portfolioId }: Props): JSX.Element {
    const [ getErrorText, setGetErrorText ] = useState<GetErrorText<MigrationPeriodToSave>>(() => () => '');
    
    const onChange = async (value: string | Date | number | boolean, property: keyof MigrationPeriodToSave, triggerPreview?: boolean) => {
        const newPeriodToSave = {...autoCreateDetails.migrationPeriodToSave, [property]: value};
        const validationErrors: ValidationError[] = await validate(Object.assign(new MigrationPeriodToSave(), newPeriodToSave));
        const getErrorTextFn = errorLookup<MigrationPeriodToSave>(validationErrors);
        setGetErrorText(() => getErrorTextFn);
        const isInvalid = validationErrors.length > 0 || !newPeriodToSave.startDate || !newPeriodToSave.endDate
            || !newPeriodToSave.waveDurationInWeeks || !newPeriodToSave.numberOfWaves;
        setAutoCreateDetails({ ...autoCreateDetails, migrationPeriodToSave: newPeriodToSave, isInvalid });
        if (!isInvalid && triggerPreview) {
            getWavesPreview(newPeriodToSave);
        }
    };

    /**
     * Convert the string date from polaris date picker to a Date type
     * date picker value is in the format: YYYY-MM-DD
     * @param value 
     * @param property 
     */
    const onDateChanged = (value: string, property: keyof MigrationPeriodToSave, time: string, triggerPreview?: boolean) => {
        const date: Date = new Date(`${value}${time}`);
        onChange(date, property, triggerPreview);
    };

    async function getWavesPreview(newPeriodToSave: MigrationPeriodToSave) {
        setAutoCreateDetails({...autoCreateDetails, migrationPeriodToSave: newPeriodToSave, previewLoading: true});
        try {
            const previewWaves = await migrationWavesService.createWavesPreview(portfolioId, newPeriodToSave as MigrationPeriod);
            setAutoCreateDetails({
                ...autoCreateDetails,
                migrationPeriodToSave: newPeriodToSave,
                previewLoading: false,
                previewWaves,
                previewErrMsg: '',
                isInvalid: false});
        } catch (err) {
            setAutoCreateDetails({
                ...autoCreateDetails,
                migrationPeriodToSave: newPeriodToSave,
                previewLoading: false,
                previewWaves: [],
                previewErrMsg: (err as AxiosError<AxiosErrorResponse>)?.response?.data?.message || '',
                isInvalid: true,
            });
        }
    };

    const migrationPeriodToSave = autoCreateDetails.migrationPeriodToSave;
    const columnDefs: TableProps.ColumnDefinition<MigrationWave>[] = [{
        id: 'name',
        header: constants.NAME,
        cell: item => item.name,
    }, {
        id: 'startDate',
        header: constants.START_DATE,
        cell: item => toUTCShortDate(new Date(item.startDate)), // display the date in UTC to be consistent across timezones
    }, {
        id: 'endDate',
        header: constants.END_DATE,
        cell: item => toUTCShortDate(new Date(item.endDate)), // display the date in UTC to be consistent across timezones
    }];
    return (
        <SpaceBetween size='s'>
            <h3>{constants.GENERAL_SETTINGS}</h3>
            <ColumnLayout columns={2}>
                <FormField label={constants.WAVE_DURATION_IN_WEEKS}
                    errorText={getErrorText('waveDurationInWeeks')}>
                    <Input
                        disabled={autoCreateDetails.previewLoading}
                        type='number'
                        placeholder={constants.WAVE_DURATION_IN_WEEKS}
                        value={`${migrationPeriodToSave?.waveDurationInWeeks}`}
                        onChange={({ detail }) => onChange(Number(detail.value), 'waveDurationInWeeks', true)} />
                </FormField>
                <FormField label={constants.NUMBER_OF_WAVES} errorText={getErrorText('numberOfWaves')}>
                    <Input
                        disabled={autoCreateDetails.previewLoading}
                        type='number'
                        placeholder={constants.NUMBER_OF_WAVES}
                        value={`${migrationPeriodToSave?.numberOfWaves}`}
                        onChange={({ detail }) => onChange(Number(detail.value), 'numberOfWaves', true)} />
                </FormField>
            </ColumnLayout>
            <ColumnLayout columns={2}>
                <FormField label={constants.MIGRATION_START_DATE} errorText={getErrorText('startDate')}>
                    <DatePicker
                        disabled={autoCreateDetails.previewLoading}
                        nextMonthAriaLabel={constants.NEXT_MONTH}
                        placeholder={DATE_PLACEHOLDER}
                        previousMonthAriaLabel={constants.PREVIOUS_MONTH}
                        todayAriaLabel={constants.TODAY}
                        // display the date in UTC to be consistent across timezones
                        value={migrationPeriodToSave?.startDate ? toUTCShortDate(new Date(migrationPeriodToSave.startDate)) : ''}
                        onChange={({ detail }) => onDateChanged(detail.value, 'startDate', WAVE_START_TIME, true)} />
                </FormField>
                <FormField label={constants.MIGRATION_END_DATE} errorText={getErrorText('endDate')}>
                    <DatePicker
                        disabled={autoCreateDetails.previewLoading}
                        nextMonthAriaLabel={constants.NEXT_MONTH}
                        placeholder={DATE_PLACEHOLDER}
                        previousMonthAriaLabel={constants.PREVIOUS_MONTH}
                        todayAriaLabel={constants.TODAY}
                        // display the date in UTC to be consistent across timezones
                        value={migrationPeriodToSave?.endDate ? toUTCShortDate(new Date(migrationPeriodToSave.endDate)) : ''}
                        onChange={({ detail }) => onDateChanged(detail.value, 'endDate', WAVE_END_TIME, true)} />
                </FormField>
            </ColumnLayout>
            <Box fontWeight='bold'>{constants.START_NEW_WAVE_ON}</Box>
            <RadioGroup
                items={START_DAY_OPTIONS}
                value={migrationPeriodToSave?.startDayOption || START_DAY_OPTIONS[0].value}
                onChange={({ detail }) => onChange(detail.value, 'startDayOption', true)}
            />
            <h3>{constants.WAVE_CAPACITY}</h3>
            <ColumnLayout columns={3}>
                <FormField label={constants.APPLICATIONS} errorText={getErrorText('applicationCapacity')}>
                    <Input
                        disabled={autoCreateDetails.previewLoading}
                        placeholder={`${constants.DEFAULT} ${DEFAULT_APP_CAPACITY}`}
                        type='number'
                        value={`${migrationPeriodToSave?.applicationCapacity}` || ''}
                        onChange={({ detail }) => onChange(Number(detail.value), 'applicationCapacity')} />
                </FormField>
                <FormField label={constants.SERVERS} errorText={getErrorText('serverCapacity')}>
                    <Input
                        disabled={autoCreateDetails.previewLoading}
                        placeholder={`${constants.DEFAULT} ${DEFAULT_SERVER_CAPACITY}`}
                        type='number'
                        value={`${migrationPeriodToSave?.serverCapacity}` || ''}
                        onChange={({ detail }) => onChange(Number(detail.value), 'serverCapacity')} />
                </FormField>
                <FormField label={constants.DATABASES} errorText={getErrorText('databaseCapacity')}>
                    <Input
                        disabled={autoCreateDetails.previewLoading}
                        placeholder={`${constants.DEFAULT} ${DEFAULT_DB_CAPACITY}`}
                        type='number'
                        value={`${migrationPeriodToSave?.databaseCapacity}` || ''}
                        onChange={({ detail }) => onChange(Number(detail.value), 'databaseCapacity')} />
                </FormField>
            </ColumnLayout>
            <Checkbox
                checked={migrationPeriodToSave?.deleteExistingWaves || false}
                onChange={({ detail }) => onChange(detail.checked, 'deleteExistingWaves')}>
                {constants.DELETE_EXISTING_WAVES}
            </Checkbox>
            { autoCreateDetails.previewLoading ?
                <Spinner size='large'></Spinner> : (
                    autoCreateDetails.previewErrMsg ? <Alert type='error'>{autoCreateDetails.previewErrMsg}</Alert>: 
                        (autoCreateDetails.previewWaves.length ?
                            <Table columnDefinitions={columnDefs} items={autoCreateDetails.previewWaves} />: null)                  
                ) }
        </SpaceBetween>
    );
}

export default AutoCreateWaves;
